// Форматирование для журнала сроков. Сервер отдаёт даты строками по Алматы (YYYY-MM-DD[ HH:mm[:ss]]),
// поэтому разбираем их как строки - без new Date(), чтобы часовой пояс браузера ничего не сдвигал
import { plural } from '../Documents/format';
import { Granularity } from './types';

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

const STAMP = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/;

/** 'YYYY-MM-DD HH:mm' → '01.10.26 12:30' */
export function fmtStamp(s?: string | null): string {
  const m = s ? STAMP.exec(s) : null;
  if (!m) return '-';
  const date = `${m[3]}.${m[2]}.${m[1].slice(2)}`;
  return m[4] ? `${date} ${m[4]}:${m[5]}` : date;
}

/** 'YYYY-MM-DD' → '01.10.2026' */
export function fmtDay(s?: string | null): string {
  const m = s ? STAMP.exec(s) : null;
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '-';
}

/** 'YYYY-MM-DD' → '1 октября 2026' */
export function fmtDayLong(s?: string | null): string {
  const m = s ? STAMP.exec(s) : null;
  return m ? `${Number(m[3])} ${MONTHS_GEN[Number(m[2]) - 1]} ${m[1]}` : '-';
}

/** 'YYYY-MM' → 'Октябрь 2026' */
export function monthLabel(ym?: string | null): string {
  const m = ym ? /^(\d{4})-(\d{2})/.exec(ym) : null;
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : 'Все периоды';
}

/** 'YYYY-MM' → 'окт 26' */
export function monthShort(ym: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(ym);
  return m ? `${MONTHS_SHORT[Number(m[2]) - 1]} ${m[1].slice(2)}` : ym;
}

/** Подпись точки на оси: день → '05.10', месяц → 'окт 26' */
export function axisLabel(key: string, granularity: Granularity): string {
  if (granularity === 'month') return monthShort(key);
  const m = STAMP.exec(key);
  return m ? `${m[3]}.${m[2]}` : key;
}

/** Полная подпись точки во всплывающей подсказке */
export function pointLabel(key: string, granularity: Granularity): string {
  return granularity === 'month' ? monthLabel(key) : fmtDayLong(key);
}

/** Предыдущий месяц для 'YYYY-MM' */
export function prevMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** Номер месяца от начала эры - для разницы месяцев между 'YYYY-MM' */
const monthIndex = (ym: string): number => {
  const [y, m] = ym.split('-').map(Number);
  return y * 12 + (m - 1);
};

/** Сколько месяцев от from до to ('YYYY-MM'): отрицательное - to раньше */
export function monthDiff(from: string, to: string): number {
  return monthIndex(to.slice(0, 7)) - monthIndex(from.slice(0, 7));
}

/** Последний день месяца 'YYYY-MM' → 'YYYY-MM-DD' */
export function monthEnd(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${ym}-${String(days).padStart(2, '0')}`;
}

/** Значение со знаком: '+1 250 ₸' / '−300 ₸' / '' для нуля */
export function signed(v: number, fmt: (x: number) => string): string {
  if (v > 0) return `+${fmt(v)}`;
  if (v < 0) return `−${fmt(Math.abs(v))}`;
  return '';
}

/** Компактная сумма для осей и плиток: 1 250 000 → '1,25 млн' */
export function fmtCompact(v?: number | null): string {
  if (v === null || v === undefined || !isFinite(v)) return '-';
  const a = Math.abs(v);
  const f = (n: number, d: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: d });
  if (a >= 1e9) return `${f(v / 1e9, 2)} млрд`;
  if (a >= 1e6) return `${f(v / 1e6, a >= 1e8 ? 0 : a >= 1e7 ? 1 : 2)} млн`;
  if (a >= 1e3) return `${f(v / 1e3, a >= 1e5 ? 0 : 1)} тыс`;
  return f(v, 0);
}

/** Процент с одной цифрой после запятой */
export function fmtPct(v?: number | null, digits = 1): string {
  if (v === null || v === undefined || !isFinite(v)) return '-';
  return `${v.toLocaleString('ru-RU', { maximumFractionDigits: digits })}%`;
}

/** Доля part от total в процентах (0, если total пуст) */
export function share(part: number, total: number): number {
  return total ? (part / total) * 100 : 0;
}

/** Изменение к прошлому периоду, %: null - сравнивать не с чем */
export function deltaPct(cur: number, prev?: number | null): number | null {
  if (prev === null || prev === undefined || !prev) return null;
  return ((cur - prev) / Math.abs(prev)) * 100;
}

/** Подпись изменения: '+12,4%' / '−3%' */
export function fmtDelta(d: number | null): string {
  if (d === null || !isFinite(d)) return '';
  const sign = d > 0 ? '+' : d < 0 ? '−' : '';
  return `${sign}${Math.abs(d).toLocaleString('ru-RU', { maximumFractionDigits: Math.abs(d) >= 100 ? 0 : 1 })}%`;
}

/** Сколько осталось до истечения срока: 'просрочено 5 дн.' / 'сегодня' / '45 дн.' / '1 г. 3 мес.' */
export function daysLeftLabel(days: number | null): string {
  if (days === null) return 'не указан';
  if (days < 0) return `просрочено ${fmtDuration(-days)}`;
  if (days === 0) return 'истекает сегодня';
  return fmtDuration(days);
}

/** Длительность в днях: до 90 - днями, дальше - месяцами и годами */
export function fmtDuration(days: number): string {
  if (days <= 90) return `${days} дн.`;
  const months = Math.round(days / 30.44);
  if (months < 12) return `${months} мес.`;
  const y = Math.floor(months / 12);
  const rest = months % 12;
  return rest ? `${y} г. ${rest} мес.` : `${y} ${plural(y, 'год', 'года', 'лет')}`;
}

/** «Давность» последней записи: 'сегодня' / '3 дня назад' */
export function agoLabel(days: number | null): string {
  if (days === null) return 'нет записей';
  if (days <= 0) return 'сегодня';
  if (days === 1) return 'вчера';
  return `${days} ${plural(days, 'день', 'дня', 'дней')} назад`;
}

/** Количество упаковок: дробное - до трёх знаков (частичные упаковки) */
export function fmtQty(v: number): string {
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
}

/** Целое с разделителями разрядов */
export function fmtInt(v?: number | null): string {
  if (v === null || v === undefined) return '-';
  return Math.round(v).toLocaleString('ru-RU');
}
